import { useEffect } from 'react';

/**
 * Per-route document metadata for our CRA single-page app — sets the page title, meta
 * description, canonical URL, and any JSON-LD structured data, then restores the prior values
 * on unmount (so SPA navigation doesn't leak one route's meta onto the next).
 *
 * This helps crawlers that execute JS (Google, AI answer engines) understand each public route
 * distinctly. Note: non-JS social scrapers (Twitter/Slack/LinkedIn unfurls) only see the static
 * tags in public/index.html — per-route OG cards need prerendering/SSR, which is out of scope.
 */
export interface DocumentMeta {
  title?: string;
  description?: string;
  /** Absolute canonical URL on the primary domain (e.g. https://wisetree.ai/faq). */
  canonical?: string;
  /** One or more JSON-LD objects to inject as <script type="application/ld+json">. */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

function ensureMeta(name: string): HTMLMetaElement {
  let el = document.head.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  return el;
}

export function useDocumentMeta({ title, description, canonical, jsonLd }: DocumentMeta): void {
  const ldKey = jsonLd ? JSON.stringify(jsonLd) : '';
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const descEl = description ? ensureMeta('description') : null;
    const prevDesc = descEl?.getAttribute('content') ?? null;
    if (descEl && description) descEl.setAttribute('content', description);

    const canonicalEl = canonical ? (document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null) : null;
    const prevCanonical = canonicalEl?.href ?? null;
    if (canonicalEl && canonical) canonicalEl.href = canonical;

    const items = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
    const ldEls = items.map((obj) => {
      const ld = document.createElement('script');
      ld.type = 'application/ld+json';
      ld.text = JSON.stringify(obj);
      document.head.appendChild(ld);
      return ld;
    });

    return () => {
      document.title = prevTitle;
      if (descEl && prevDesc !== null) descEl.setAttribute('content', prevDesc);
      if (canonicalEl && prevCanonical !== null) canonicalEl.href = prevCanonical;
      ldEls.forEach((el) => el.remove());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, canonical, ldKey]);
}
